/** Index recoverable contact time once, instead of re-scanning an antenna's
 * entire schedule for every conflict/window intersection. Overlapping windows
 * retain their multiplicity, matching the original resolver's summed result. */
interface Interval {
  startMs: number;
  endMs: number;
}
interface ScheduledInterval extends Interval {
  missionId: string;
}
export type RecoverableTime = (interval: Interval) => number;

export function indexRecoverableTime(
  windows: readonly Interval[],
  schedule: readonly ScheduledInterval[],
  capacity: number,
  excludedMission: string,
): RecoverableTime {
  if (capacity <= 0 || !windows.length) return () => 0;
  const events = new Map<number, { windows: number; occupied: number }>();
  const add = (t: number, windows: number, occupied: number) => {
    const event = events.get(t) ?? { windows: 0, occupied: 0 };
    event.windows += windows;
    event.occupied += occupied;
    events.set(t, event);
  };
  for (const w of windows) {
    if (w.endMs <= w.startMs) continue;
    add(w.startMs, 1, 0);
    add(w.endMs, -1, 0);
  }
  for (const s of schedule) {
    if (s.missionId === excludedMission || s.endMs <= s.startMs) continue;
    add(s.startMs, 0, 1);
    add(s.endMs, 0, -1);
  }
  const starts: number[] = [],
    ends: number[] = [],
    weights: number[] = [],
    prefix: number[] = [];
  let windowCount = 0,
    occupied = 0,
    previous = 0,
    accumulated = 0;
  for (const [time, event] of [...events].sort((a, b) => a[0] - b[0])) {
    if (windowCount > 0 && occupied < capacity && time > previous) {
      const last = ends.length - 1;
      if (last >= 0 && ends[last] === previous && weights[last] === windowCount) ends[last] = time;
      else {
        starts.push(previous);
        ends.push(time);
        weights.push(windowCount);
        prefix.push(accumulated);
      }
      accumulated += (time - previous) * windowCount;
    }
    windowCount += event.windows;
    occupied += event.occupied;
    previous = time;
  }
  const until = (time: number) => {
    let lo = 0,
      hi = starts.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (starts[mid] < time) lo = mid + 1;
      else hi = mid;
    }
    const i = lo - 1;
    return i < 0 ? 0 : prefix[i] + (Math.min(time, ends[i]) - starts[i]) * weights[i];
  };
  return ({ startMs, endMs }) => (endMs > startMs ? until(endMs) - until(startMs) : 0);
}
